import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCircleCheck, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import './pendingClientRequests.css';

/**
 * Review modal for register_client (PLAN_registration.md).
 *
 * Unlike every other request, the ACCOUNT ALREADY EXISTS — the WhatsApp code
 * created it. So there is nothing to materialize and NOTHING TO REJECT: the
 * only actions are Acknowledge (clears the queue row) and opening the client
 * page to look closer or fix details. The backend refuses reject for this
 * type; this modal simply doesn't offer it.
 */
export default function RegistrationReview({ request, onClose, onDone, readOnlyOutcome = null }) {
    const navigate = useNavigate();
    const [docs, setDocs] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const p = request.payload || {};
    const clientId = request.client_id;

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`/api/clients/${clientId}/documents`, { withCredentials: true });
                setDocs(Array.isArray(res.data) ? res.data : []);
            } catch (e) {
                setDocs([]); // the section degrades to "none on file"
            }
        })();
    }, [clientId]);

    const acknowledge = async () => {
        setSubmitting(true);
        setError('');
        try {
            const res = await axios.post(`/api/client-change-requests/${request.id}/approve`, {});
            onDone('approved', res.data?.message || 'Registration acknowledged.', clientId);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to acknowledge the registration.');
            setSubmitting(false);
        }
    };

    const hasDoc = (type) => (docs || []).some((d) => d.type === type);
    const billingDone = String(p.billing_completed) === 'true' || p.billing_completed === true;
    const isBusiness = p.client_type === 'business';

    const DocChip = ({ ok, label }) => (
        <span className={`pcr-chip ${ok ? 'pcr-chip--ok' : 'pcr-chip--warn'}`}>
            <FontAwesomeIcon icon={ok ? faCircleCheck : faHourglassHalf} /> {label}
        </span>
    );

    return (
        <div className="pcr-modal-overlay" onClick={() => !submitting && onClose()}>
            <div className="pcr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pcr-modal-header">
                    <div>
                        <h3>New Client Registration</h3>
                        <div className="pcr-modal-sub">
                            {request.client_display_name || request.client_company_name}
                            {' · self-registered '}
                            {p.client_type === 'business' ? 'business' : 'individual'}
                        </div>
                    </div>
                    <button className="pcr-modal-close" onClick={() => !submitting && onClose()} title="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="pcr-modal-body">
                    <div className="pcr-section">
                        <div className="pcr-section-title">Account</div>
                        <div className="pcr-fields">
                            <div className="pcr-field"><span>Type</span>{isBusiness ? 'Business' : 'Individual'}</div>
                            {/* Display name is auto-generated: company name for a
                                business, first + last name for an individual. */}
                            <div className="pcr-field">
                                <span>Display name</span>
                                {request.client_display_name || request.client_company_name || '—'}
                            </div>
                            {isBusiness && (
                                <div className="pcr-field"><span>Company</span>{p.company_name || '—'}</div>
                            )}
                            <div className="pcr-field">
                                <span>Contact person</span>
                                {[request.requested_by_first_name, request.requested_by_last_name].filter(Boolean).join(' ') || '—'}
                            </div>
                            <div className="pcr-field"><span>Email</span>{p.email || '—'}</div>
                            <div className="pcr-field"><span>WhatsApp</span>{p.phone || '—'} <em>(verified)</em></div>
                            <div className="pcr-field"><span>Terms accepted</span>version {p.terms_version || '—'}</div>
                        </div>
                    </div>

                    <div className="pcr-section">
                        <div className="pcr-section-title">Setup progress</div>
                        <div className="pcr-chips">
                            <DocChip ok={billingDone} label={billingDone ? 'Billing details saved' : 'Billing details missing'} />
                            {isBusiness && (
                                <DocChip ok={hasDoc('trade_license')}
                                    label={hasDoc('trade_license') ? 'Trade license on file' : 'Trade license missing'} />
                            )}
                            {hasDoc('vat_certificate') && <DocChip ok label="VAT certificate on file" />}
                        </div>
                        <div className="pcr-hint" style={{ marginTop: 8 }}>
                            The account is already live — acknowledging clears this from the
                            queue. Anything wrong with the details? Fix them on the client
                            page; a registration can't be rejected.
                        </div>
                    </div>

                    {error && <div className="pcr-error">{error}</div>}
                </div>

                {/* Read-only history view (2026-08-06): rejected/
                    cancelled/approved rows open for inspection — outcome
                    instead of action buttons. */}
                {readOnlyOutcome ? (
                    <div className="pcr-modal-actions" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{readOnlyOutcome.statusLabel}</strong>
                            {readOnlyOutcome.reviewerName ? <> by <strong>{readOnlyOutcome.reviewerName}</strong></> : null}
                            {readOnlyOutcome.reviewedAt ? <> · {readOnlyOutcome.reviewedAt}</> : null}
                            {readOnlyOutcome.reason ? <div><strong>Reason:</strong> {readOnlyOutcome.reason}</div> : null}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {readOnlyOutcome.onOpenEntity ? (
                                <button className="pcr-btn pcr-btn--primary" onClick={readOnlyOutcome.onOpenEntity}>
                                    {readOnlyOutcome.entityLabel}
                                </button>
                            ) : null}
                            <button className="pcr-btn pcr-btn--ghost" onClick={onClose}>Close</button>
                        </div>
                    </div>
                ) : (
                <div className="pcr-modal-actions">
                    <button
                        type="button"
                        className="pcr-btn pcr-btn--ghost"
                        onClick={() => navigate(`/clients/${clientId}`)}
                    >
                        Open client page
                    </button>
                    <button
                        type="button"
                        className="pcr-btn pcr-btn--primary"
                        onClick={acknowledge}
                        disabled={submitting}
                    >
                        {submitting ? 'Saving…' : 'Acknowledge'}
                    </button>
                </div>
                )}
            </div>
        </div>
    );
}
